/**
 * Canonical enum definitions for the data layer.
 *
 * These enums represent core data-domain concepts (product classification,
 * trade direction/flow, location detail level) that are shared across the
 * entire application. Visualization-specific enums (VizType, UIView,
 * ColorBy, etc.) now live in `@atlas-int/viz`.
 */

import type {
  ProductClass as GqlProductClass,
  ServicesClass as GqlServicesClass,
} from "../graphql/__generated__/graphql";

// ── Compile-time assertions ──────────────────────────────────────
// Ensure domain enum VALUES stay aligned with the GraphQL codegen types.
// If the GraphQL schema changes, these will produce a TS error here
// rather than silently diverging at runtime.

/** Resolves to `true` when every value in Domain is also a value in Gql. */
type ValuesAreSubset<
  Domain extends Record<string, string>,
  Gql extends Record<string, string>,
> = Domain[keyof Domain] extends Gql[keyof Gql] ? true : never;

// -- Product classification --

/**
 * Canonical product classification enum for the explore tool.
 *
 * String values match the GraphQL schema `ProductClass` enum values.
 * Country-profiles uses `CPProductClass` (in src/types/countryProfileEnums.ts)
 * which has different string values for backward compatibility with a different API.
 */
export enum ProductClass {
  HS92Products = "HS92",
  HS12Products = "HS12",
  HS22Products = "HS22",
  SITCProducts = "SITC",
}

/** Services classification */
export enum ServicesClass {
  bilateral = "bilateral",
  unilateral = "unilateral",
}

// Every ProductClass value must be a valid GQL ProductClass value.
// If codegen regenerates with different values, this line will error.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _AssertProductClassAligned = ValuesAreSubset<typeof ProductClass, typeof GqlProductClass>;

// NOTE: ServicesClass intentionally includes "bilateral" which is NOT in the GQL schema.
// The GQL schema only defines "unilateral". The "bilateral" value is used for
// client-side filtering/UI purposes and is stripped before sending to the API.
// The reverse check ensures every GQL ServicesClass value exists in our domain enum.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _AssertGqlServicesClassCovered = GqlServicesClass extends ServicesClass ? true : never;

/**
 * Canonical product level (depth) enum for the explore tool.
 *
 * Uses numeric values representing the number of digits in the product code.
 * Country-profiles uses a string-valued `CPProductLevel` with values like
 * "section", "twoDigit", "fourDigit", "sixDigit".
 */
export enum ProductLevel {
  ProductSection = 1,
  Product2digit = 2,
  Product4digit = 4,
  Product6digit = 6,
  /** Sentinel value representing "all products" (used in product dropdowns). */
  AllProducts = 9,
}

// -- Trade direction & flow --

/**
 * Canonical trade direction enum for the explore tool.
 *
 * Uses lowercase string values ("exports", "imports") for URL routing.
 * Country-profiles uses a different `CPTradeDirection` with singular values
 * ("export", "import").
 */
export enum TradeDirection {
  Exports = "exports",
  Imports = "imports",
}

/**
 * Canonical trade flow enum for the explore tool.
 *
 * Uses lowercase string values ("gross", "net") for URL routing.
 * Country-profiles uses a different `CPTradeFlow` with capitalized values
 * ("Gross", "Net").
 */
export enum TradeFlow {
  Gross = "gross",
  Net = "net",
}

// -- Location detail level --

/**
 * Canonical location detail level for the explore tool.
 *
 * Controls the granularity of location data displayed in visualizations.
 */
export enum LocationDetailLevel {
  country = "country",
  subregion = "subregion",
  region = "region",
}
